const openings = [
  "夜色像一层薄墨缓缓铺开，天地之间只剩下风声与远处隐约的灯火。",
  "天色将明未明，檐角的残雪被风卷起，细碎地散落在青石台阶上。",
  "云层压得很低，仿佛一伸手便能触到那片翻涌的灰白。",
  "雨下了整整一夜，等停下来的时候，整座城都像被洗过一遍。",
  "晨光越过山脊，最先照亮的是那道孤独站立的身影。",
  "屋子里很静，只有烛火偶尔爆出一声轻响。",
  "街市上的人声渐渐稀了，摊贩收起最后一面幌子。",
  "剑鞘上的铜环轻轻一响，惊醒了倚在门边的少年。",
];

const narration = [
  "他站在原地很久没有动，像是在等一个早已不会出现的人。",
  "许多年后回想起这一刻，他才明白命运的转折往往悄无声息。",
  "这条路他走过无数遍，可今天每一步都显得格外沉重。",
  "旁人只看见他的从容，却没人知道那从容背后藏着多少筹谋。",
  "有些话一旦说出口，就再也收不回来了。",
  "他把要说的话在心里过了一遍，最后还是咽了回去。",
  "远处的钟声一声接一声，敲得人心里发慌。",
  "空气中弥漫着雨水和草木混合的气味，让人莫名安定。",
  "她知道，从踏进这道门槛起，就再没有回头的可能。",
];

const dialogue = [
  "“你不该来的。”那人背对着他，声音低得几乎听不清。",
  "“若我偏要来呢？”他笑了一下，笑意却没有到达眼底。",
  "“……值得吗？”她终于问出了这句压在心底很久的话。",
  "“值不值得，等我做完再说。”他答得干脆。",
  "“你可知这背后要付出什么代价？”老人的目光沉沉。",
  "“代价我早就付过了，从很多年前开始。”",
  "“别逞强。”她轻声说，“你一个人扛不住这么多。”",
  "“所以我把你带来了。”他转头看她，眼神很亮。",
];

const action = [
  "话音未落，那一刀已经贴着他的脖颈落了下来。",
  "剑光乍起，如同一线清冷的月华划破长夜。",
  "他侧身避过，脚尖在湿滑的石面上一转，反手拍出一掌。",
  "气浪翻涌，四周的落叶被震得四散飞起。",
  "两人交手只在电光石火之间，旁观者甚至没看清招式。",
  "她抬眼的一瞬，衣袖下的符箓已经无声燃起。",
  "轰鸣声里，那道身影穿过烟尘，稳稳落在断墙之上。",
  "刀锋入土的刹那，整座院子都安静了下来。",
];

const reflection = [
  "他忽然想起很多年前的自己，那时还没有这么多放不下的东西。",
  "原来真正的强大，不是从不受伤，而是受伤之后依然愿意往前走。",
  "她终于明白，所谓长大，就是把哭声一点点调成静音的过程。",
  "这世上的遗憾大多相似，无非是来不及，和再也不见。",
  "有些坚持说给旁人听是无谓，可对自己而言，那是全部的答案。",
  "他望着天边渐次亮起的星子，心里忽然踏实了一些。",
  "路还很长，但至少方向是对的。",
];

const closures = [
  "风又起了，卷着夜色一路向北而去。",
  "他最后看了一眼那座城，转身走进了更深的夜里。",
  "天就快亮了，她想，明天总会不一样。",
  "这一刻，喧嚣都退到了很远的地方。",
  "一切都才刚刚开始。",
];

function hashSeed(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function makeRandom(seed: number) {
  let state = seed || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 4294967296;
  };
}

function pick<T>(arr: T[], rnd: () => number, used: Set<string>): T {
  let item = arr[Math.floor(rnd() * arr.length)];
  let guard = 0;
  while (used.has(String(item)) && guard < arr.length) {
    item = arr[Math.floor(rnd() * arr.length)];
    guard += 1;
  }
  used.add(String(item));
  return item;
}

/**
 * Deterministically generates readable chapter prose from the chapter key.
 * No network/database required — purely local demo content.
 */
export function generateChapterParagraphs(
  novelId: string,
  chapterIndex: number,
  chapterTitle: string,
): string[] {
  const rnd = makeRandom(hashSeed(`${novelId}#${chapterIndex}`));
  const used = new Set<string>();
  const paragraphCount = 9 + Math.floor(rnd() * 3);
  const paragraphs: string[] = [];

  const lede = chapterTitle.replace(/^第[一二三四五六七八九十百千零]+章\s*/, "");
  paragraphs.push(
    `　　${pick(openings, rnd, used)}${lede ? `这一章的故事，从一个并不起眼的清晨开始。` : ""}`,
  );

  for (let i = 1; i < paragraphCount; i += 1) {
    const pool = i % 3 === 0 ? dialogue : i % 4 === 0 ? action : narration;
    const second = i % 2 === 0 ? pick(reflection, rnd, used) : pick(narration, rnd, used);
    paragraphs.push(`　　${pick(pool, rnd, used)}${second}`);
  }

  paragraphs.push(`　　${pick(closures, rnd, used)}`);
  return paragraphs;
}